/**
 * Where a record's full page is (optum-agent Feature 87, D17, D18).
 *
 * One map from record kind to full page serves the panel's Open button, a
 * double-click on a row or card, and Shift+Enter on a focused one. A kind
 * with no page of its own (staff, a group, a collection, a service token)
 * maps to nothing, and those gestures open its panel as one click does.
 *
 * An organization user's page is their account, named by a subject the ref
 * does not carry: the users table passes it, and the user's panel builds it
 * from the user it has read.
 */
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { linkageCanvasPath } from '../../features/automation/paths.js';
import { parsePluginId } from '../../features/plugins/plugin-id.js';
import { useCurrentOrgId } from '../../lib/use-current-org.js';
import { RECORD_PARAM, useRecordPanel, type RecordRef } from './record.js';

/** What a page path depends on besides the ref: the workspace and the list it came from. */
export interface PageScope {
  /** The organization workspace the record was opened in; null on the platform. */
  orgId: string | null;
  /** The list's query string, which a task's page keeps so it can walk the list. */
  search: string;
}

/** The full page of `ref`, or null when its kind has none. */
export function recordPage(ref: RecordRef, scope: PageScope): string | null {
  const id = encodeURIComponent(ref.id);
  switch (ref.kind) {
    case 'organization':
      return `/orgs/${id}/overview`;
    case 'account':
      return `/accounts/${id}`;
    case 'agent':
      return ref.orgId ? `/orgs/${encodeURIComponent(ref.orgId)}/agents/${id}` : null;
    case 'task': {
      const list = new URLSearchParams(scope.search);
      list.delete(RECORD_PARAM);
      const qs = list.toString();
      const base = scope.orgId ? `/orgs/${encodeURIComponent(scope.orgId)}` : '';
      return `${base}/tasks/${id}${qs ? `?${qs}` : ''}`;
    }
    case 'partner':
      return `/federation/partners/${id}`;
    case 'plugin': {
      const parsed = parsePluginId(ref.id);
      return parsed ? `/plugins/${parsed.kind}/${encodeURIComponent(parsed.name)}` : null;
    }
    case 'linkage':
    case 'org-linkage':
      return linkageCanvasPath(ref.id);
    case 'org-user':
    case 'staff':
    case 'group':
    case 'collection':
    case 'service-token':
      return null;
  }
}

/** `recordPage` in the current workspace and list. */
export function useRecordPage(): (ref: RecordRef) => string | null {
  const orgId = useCurrentOrgId();
  const { search } = useLocation();
  return useCallback((ref: RecordRef) => recordPage(ref, { orgId, search }), [orgId, search]);
}

/**
 * Go to a record's full page; `page` names one the map cannot, such as an
 * organization user's account. With neither, the record opens in its panel.
 */
export function useOpenRecordPage(): (ref: RecordRef, page?: string | null) => void {
  const pageOf = useRecordPage();
  const navigate = useNavigate();
  const panel = useRecordPanel();
  return useCallback(
    (ref: RecordRef, page?: string | null) => {
      const to = page ?? pageOf(ref);
      if (to) navigate(to);
      else panel.open(ref);
    },
    [pageOf, navigate, panel],
  );
}
